import React, {useEffect, useImperativeHandle, useRef, useState} from 'react'
import Text from '../../../components/text/Text'
import WizModal from '../../../components/modal/WizModal'
import useQueryVolumeLeagueCampaign from '../../../hooks/api/volume_league/useQueryVolumeLeagueCampaign'
import useQueryVolumeLeagueTraderVolume from '../../../hooks/api/volume_league/useQueryVolumeLeagueTraderVolume'
import WizImage from '../../../components/image/WizImage'
import {useDisplayNumber} from '../../../utils/number'
import WizSpinner from '../../../components/spinner/WizSpinner'
import {Skeleton} from 'antd'
import SkeletonNode from 'antd/es/skeleton/Node'
import SkeletonButton from 'antd/es/skeleton/Button'
import {ITraderMapCampaign} from '../../../apis/tradermap_campaign/list'
import {useTime} from '../../../utils/time'

interface IProps {
    className?: string
    userId?: string
}

const UserTradermapModal = ({className, userId}: IProps, ref: any) => {
    const modalRef = useRef<any>()
    const [symbol, setSymbol] = useState('')
    const [campaignId, setCampaignId] = useState('')
    const [campaignData, setCampaignData] = useState<ITraderMapCampaign>(null)
    // 임시: campaigns/volume API 대신 volume-league 의 competition 리그 기준으로 조회 (campaignId = league id)
    const {data: campaign} = useQueryVolumeLeagueCampaign()
    const {data, isLoading} = useQueryVolumeLeagueTraderVolume({league_id: campaignId, user_id: userId})
    const {renderPercent, displayAmount, toFixedNumber, floorBalance} = useDisplayNumber()
    const {formatUTCDate} = useTime()

    useImperativeHandle(ref, () => ({
        show: () => {
            modalRef.current.show()
        },
    }))

    useEffect(() => {
        setSymbol(campaign?.at(0)?.symbol)
        setCampaignId(campaign?.at(0)?.id)
    }, [campaign])

    useEffect(() => {
        setCampaignData(campaign?.find(i => i?.id === campaignId))
    }, [campaignId, campaign])

    return (
        <>
            <WizModal
                onClose={() => {}}
                title={'Tradermap Volume (Competition)'}
                className={`${className} max-h-[80vh] !w-auto max-w-[600px] sm:max-w-[100%]`}
                ref={modalRef}>
                <div className="flex flex-wrap items-center gap-3">
                    {campaign
                        ?.sort((a, b) => new Date(b?.end_at)?.getTime() - new Date(a?.end_at)?.getTime())
                        ?.map(item => {
                            return (
                                <div
                                    key={item?.id}
                                    onClick={() => {
                                        setSymbol(item?.symbol)
                                        setCampaignId(item?.id)
                                    }}
                                    className={`border flex items-center gap-x-1 rounded-[5px] py-[10px] px-4 cursor-pointer ${
                                        campaignId === item?.id
                                            ? 'bg-primary border-primary '
                                            : 'border-line02 hover:border-primary bg-white'
                                    }`}>
                                    <WizImage src={item?.symbol_image} className="w-7 h-7 rounded-full" />
                                    <Text className={`${campaignId === item?.id ? 'text-white' : 'text-font01'}`}>
                                        {item?.symbol}
                                    </Text>
                                </div>
                            )
                        })}
                </div>
                {!isLoading && !data?.volume ? (
                    <div className="py-10">
                        <Text className="text-font03 text-center">No Volume Data </Text>
                    </div>
                ) : (
                    <div className="mt-5 border-t border-line02 pt-5">
                        <div className="flex items-center gap-x-10 justify-center text-center">
                            <div>
                                <Text className="text-ti3 text-font02">Rank</Text>
                                <Text className="text-ti2 text-font01">{data?.rank}</Text>
                            </div>
                            <div>
                                <Text className="text-ti3 text-font02">Trade Count</Text>
                                <Text className="text-ti2 text-font01">{displayAmount(data?.trade_count)}</Text>
                            </div>
                            <div>
                                <Text className="text-ti3 text-font02">Trader Share</Text>
                                <Text className="text-ti2 text-font01">{renderPercent(data?.volume_share, 100)}</Text>
                            </div>
                            <div>
                                <Text className="text-ti3 text-font02">Date</Text>
                                <Text className="text-ti2 text-font01">
                                    {formatUTCDate(new Date(campaignData?.start_at)?.getTime(), 9)} -{' '}
                                    {formatUTCDate(new Date(campaignData?.end_at)?.getTime(), 9)}
                                </Text>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-x-2 text-center mt-6">
                            <div>
                                <Text className="text-ti3 text-font02">Buy Volume</Text>
                                <Text className="text-ti2 text-primary">{floorBalance(data?.buy_volume || 0)}</Text>
                            </div>
                            <div>
                                <Text className="text-ti3 text-font02">Total</Text>
                                <Text className="text-ti2 text-font01">{floorBalance(data?.volume)}</Text>
                            </div>
                            <div>
                                <Text className="text-ti3 text-font02">Sell Volume</Text>
                                <Text className="text-ti2 text-font_red">{floorBalance(data?.sell_volume || 0)}</Text>
                            </div>
                        </div>
                        <div className="flex justify-center items-center gap-x-2 mt-[15px]">
                            <Text className="text-body3 text-primary">
                                {renderPercent((data?.buy_volume / data?.volume || 0) * 100, 100)}
                            </Text>
                            <div className="relative overflow-hidden rounded-[20px] w-[200px] h-[8px]  bg-font_red">
                                <div
                                    className={`absolute left-0 top-0 h-full bg-primary ${
                                        !data?.buy_volume || !data?.sell_volume ? '' : ' border-r-[2px] border-white'
                                    }`}
                                    style={{width: `${(data?.buy_volume / data?.volume || 0) * 100}%`}}
                                />
                            </div>
                            <Text className="text-body3 text-font_red">
                                {renderPercent((data?.sell_volume / data?.volume || 0) * 100, 100)}
                            </Text>
                        </div>
                    </div>
                )}
            </WizModal>
        </>
    )
}

export default React.forwardRef(UserTradermapModal)
